import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Cake } from 'lucide-react';

const Login = ({ setUser }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [isForgotOpen, setIsForgotOpen] = useState(false);

  const demoEnabled = process.env.REACT_APP_DEMO_MODE_ENABLED === 'true';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      localStorage.setItem('token', response.data.access_token);
      setUser(response.data.user);
      toast.success('Login realizado com sucesso!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleDemo = async () => {
    setDemoLoading(true);
    try {
      const response = await axios.post(`${API}/auth/demo-session`);
      localStorage.setItem('token', response.data.access_token);
      setUser(response.data.user);
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível abrir a demonstração');
    } finally {
      setDemoLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-pink-50 via-rose-50 to-pink-100">
      <Card className="w-full max-w-md shadow-2xl border-pink-100 bg-white/90 backdrop-blur-sm" data-testid="login-card">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-pink-500 to-rose-400 rounded-2xl flex items-center justify-center shadow-lg">
            <Cake className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-3xl font-bold bg-gradient-to-r from-pink-600 to-rose-500 bg-clip-text text-transparent">
            SweetManager
          </CardTitle>
          <CardDescription className="text-base">Entre na sua conta para gerenciar sua confeitaria</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="border-pink-200 focus:border-pink-400"
                data-testid="email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="border-pink-200 focus:border-pink-400"
                data-testid="password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-pink-500 to-rose-400 hover:from-pink-600 hover:to-rose-500 shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <button
            type="button"
            className="text-sm text-pink-600 hover:text-pink-700 font-semibold"
            onClick={() => setIsForgotOpen(true)}
            data-testid="forgot-password-button"
          >
            Esqueci minha senha
          </button>
            <p className="text-sm text-gray-600">
              Não tem uma conta?{' '}
              <Link to="/register" className="text-pink-600 hover:text-pink-700 font-semibold" data-testid="register-link">
                Cadastre-se
              </Link>
            </p>
          </div>
          {demoEnabled && (
            <div className="mt-6 border-t border-pink-100 pt-5 text-center">
              <p className="mb-3 text-sm text-gray-600">
                Conheça o Sweet Manager sem precisar criar uma conta.
              </p>
              <Button
                type="button"
                variant="outline"
                className="w-full border-pink-300 text-pink-700 hover:bg-pink-50"
                onClick={handleDemo}
                disabled={demoLoading}
                data-testid="demo-login-button"
              >
                {demoLoading ? 'Abrindo demonstração...' : 'Explorar demonstração'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      <ForgotPasswordModal isOpen={isForgotOpen} onClose={() => setIsForgotOpen(false)} />
    </div>
  );
};

export default Login;
